import React from "react";
import { BarCustomLayerProps, BarDatum } from "@nivo/bar";

interface CreateBarValueLabelsLayerOptions {
  yAxisFormat: (value: number) => string;
  groupMode: "grouped" | "stacked";
  barKeys: string[];
  textColor: string;
  isModal?: boolean;
}

// SVG layers can't measure text before rendering, so label width is estimated
// from character count (0.62em/char overshoots bold system-ui slightly, which
// errs toward shrinking — better than touching). Long labels like "$132.6B" on
// $100B+ companies otherwise crowd at quarterly pitch.
const LABEL_CHAR_WIDTH_EM = 0.62;
const MIN_LABEL_GAP = 6;
const MIN_LABEL_FONT_SIZE = 8.5;

const fitLabelFontSize = (baseSize: number, labels: string[], pitch: number): number => {
  if (!Number.isFinite(pitch)) return baseSize;
  const maxChars = labels.reduce((max, label) => Math.max(max, label.length), 0);
  if (maxChars === 0) return baseSize;
  const fitted = (pitch - MIN_LABEL_GAP) / (maxChars * LABEL_CHAR_WIDTH_EM);
  return Math.max(MIN_LABEL_FONT_SIZE, Math.min(baseSize, fitted));
};

export const createBarValueLabelsLayer = (
  options: CreateBarValueLabelsLayerOptions | (() => CreateBarValueLabelsLayerOptions)
) => {
  const BarValueLabelsLayer = <RawDatum extends BarDatum>({
    bars,
  }: BarCustomLayerProps<RawDatum>): React.ReactElement => {
    // Resolved per render: a getter-based caller keeps this component's
    // IDENTITY stable across re-renders (a new identity makes Nivo unmount and
    // remount the whole layer — per streamed token in chat) while still
    // reading fresh config.
    const { yAxisFormat, groupMode, barKeys, textColor, isModal = false } =
      typeof options === "function" ? options() : options;
    const labelFontSize = isModal ? 11.5 : 10.5;
    if (groupMode === "stacked" && barKeys.length > 1) {
      const grouped = new Map<
        string,
        { x: number; width: number; topY: number; totalValue: number }
      >();

      for (const bar of bars) {
        const idx = String(bar.data.indexValue);
        const value = (bar.data.value ?? 0) as number;
        const existing = grouped.get(idx);

        if (!existing) {
          grouped.set(idx, {
            x: bar.x,
            width: bar.width,
            topY: bar.y,
            totalValue: value,
          });
        } else {
          existing.totalValue += value;
          if (bar.y < existing.topY) {
            existing.topY = bar.y;
          }
        }
      }

      // Stacked bars are full-pitch-width like single-series bars, so their
      // total labels get the same pitch-based room test (see below): the
      // bar-width proxy hid totals on charts like Expense Breakdown even when
      // the spacing fit the label comfortably.
      const stackGroups = Array.from(grouped.entries());
      let stackPitch = Number.POSITIVE_INFINITY;
      if (stackGroups.length > 1) {
        const xs = stackGroups.map(([, group]) => group.x).sort((a, b) => a - b);
        for (let i = 1; i < xs.length; i++) {
          stackPitch = Math.min(stackPitch, xs[i] - xs[i - 1]);
        }
      }

      const stackFontSize = fitLabelFontSize(
        labelFontSize,
        stackGroups.map(([, group]) => yAxisFormat(group.totalValue)),
        stackPitch
      );

      return (
        <g>
          {stackGroups.map(([idx, group]) => {
            if (stackPitch < 42 && group.width < 35) return null;

            const labelY =
              group.totalValue >= 0 ? group.topY - 6 : group.topY + group.width + 14;

            return (
              <text
                key={idx}
                x={group.x + group.width / 2}
                y={labelY}
                textAnchor="middle"
                dominantBaseline="auto"
                style={{
                  fontSize: stackFontSize,
                  fontWeight: 700,
                  fill: textColor,
                  fontFamily: "system-ui, -apple-system, sans-serif",
                }}
              >
                {yAxisFormat(group.totalValue)}
              </text>
            );
          })}
        </g>
      );
    }

    // For single-series charts the room a label has is governed by the PITCH
    // (bar + gap), not the bar width: 12 quarterly bars in the 760px chat
    // column are ~34px wide but ~51px apart, which fits "$58.0B" fine — the
    // old bar-width test hid labels there while the wider ticker page showed
    // them (observed 2026-07-02). Grouped multi-key charts keep the per-bar
    // width test, since their labels sit over each sub-bar.
    const singleSeries = barKeys.length <= 1;
    let pitch = Number.POSITIVE_INFINITY;
    if (singleSeries && bars.length > 1) {
      const xs = Array.from(new Set(bars.map((b) => b.x))).sort((a, b) => a - b);
      for (let i = 1; i < xs.length; i++) {
        pitch = Math.min(pitch, xs[i] - xs[i - 1]);
      }
    }

    const barFontSize = singleSeries
      ? fitLabelFontSize(
          labelFontSize,
          bars.map((bar) => yAxisFormat((bar.data.value ?? 0) as number)),
          pitch
        )
      : labelFontSize;

    return (
      <g>
        {bars.map((bar) => {
          const minWidth = isModal ? 36 : (groupMode === "grouped" && barKeys.length > 1 ? 30 : 35);
          const hasRoom = singleSeries ? pitch >= 42 || bar.width >= minWidth : bar.width >= minWidth;
          if (!hasRoom || bar.data.data[bar.data.id] == null) return null;

          const value = (bar.data.value ?? 0) as number;
          const isNegative = value < 0;
          const labelY = isNegative ? bar.y + bar.height + 14 : bar.y - 6;

          return (
            <text
              key={bar.key}
              x={bar.x + bar.width / 2}
              y={labelY}
              textAnchor="middle"
              dominantBaseline="auto"
              style={{
                fontSize: barFontSize,
                fontWeight: 700,
                fill: textColor,
                fontFamily: "system-ui, -apple-system, sans-serif",
              }}
            >
              {yAxisFormat(value)}
            </text>
          );
        })}
      </g>
    );
  };

  return BarValueLabelsLayer;
};
